import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';

import { LaunchDarklyService } from '@services/launchdarkly.service';
import { OBFUSCATE_CONFIDENTIAL_INFO_FLAG } from '@utils/constants';

/** FullStory privacy classes that block an element (and its children) from session recordings. */
const FS_MASK_CLASSES = ['fs-block', 'fs-exclude'];

/**
 * Adds the FullStory masking classes (`fs-block fs-exclude`) to the host
 * element when the obfuscation flag is ON, and removes them when it is OFF.
 *
 * Apply to elements whose content is confidential but is NOT part of today's
 * always-on masking baseline (e.g. borrower name, document name, comments).
 * Reacts to mid-session flag changes so new sessions reflect the latest value
 * without a redeploy.
 *
 * Usage:
 *   `<div appFsMask>…</div>`                     always mask when flag is ON
 *   `<td [appFsMask]="isSourceStatement">…</td>` mask when flag is ON AND condition is truthy
 */
@Directive({
  selector: '[appFsMask]',
  standalone: false,
})
export class FsMaskDirective implements OnInit, OnChanges, OnDestroy {
  /**
   * Optional gate for masking this specific element. The host is masked only
   * when the obfuscation flag is ON *and* this condition is truthy. Defaults to
   * `true`, so the bare attribute form (`appFsMask`) masks whenever the flag is
   * ON. Use the bound form (`[appFsMask]="isSourceStatement"`) to scope masking
   * to confidential rows only.
   */
  @Input('appFsMask') maskWhen: boolean | '' = true;

  private _flagSub: Subscription;
  private _flagEnabled = false;

  constructor(
    private _el: ElementRef<HTMLElement>,
    private _renderer: Renderer2,
    private _launchDarklyService: LaunchDarklyService,
  ) {}

  ngOnInit(): void {
    this._flagEnabled = this._isObfuscationEnabled(this._launchDarklyService.flags);
    this._applyMask();
    this._flagSub = this._launchDarklyService.flagChange.subscribe((flags) => {
      this._flagEnabled = this._isObfuscationEnabled(flags);
      this._applyMask();
    });
  }

  ngOnChanges(): void {
    this._applyMask();
  }

  ngOnDestroy(): void {
    this._flagSub?.unsubscribe();
  }

  private _isObfuscationEnabled(flags: Object): boolean {
    return !!flags?.[OBFUSCATE_CONFIDENTIAL_INFO_FLAG];
  }

  /**
   * The bare attribute form (`appFsMask`) binds an empty string, which we treat
   * as `true`. Any bound value (`[appFsMask]="expr"`) is evaluated by
   * truthiness, so falsy conditions (`false`/`null`/`undefined`/`0`) do not mask.
   */
  private _shouldMask(): boolean {
    const gate = this.maskWhen === '' ? true : !!this.maskWhen;
    return this._flagEnabled && gate;
  }

  private _applyMask(): void {
    const enabled = this._shouldMask();
    for (const className of FS_MASK_CLASSES) {
      if (enabled) {
        this._renderer.addClass(this._el.nativeElement, className);
      } else {
        this._renderer.removeClass(this._el.nativeElement, className);
      }
    }
  }
}
